import React, { useMemo } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import GlobalShortcutsComponent, { type GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import { confluence } from '@atlassian/global-shortcuts/confluence';
import { loom } from '@atlassian/global-shortcuts/loom';
import { goals } from '@atlassian/global-shortcuts/goals';
import { opsgenie } from '@atlassian/global-shortcuts/opsgenie';
import { projects } from '@atlassian/global-shortcuts/projects';
import { teams } from '@atlassian/global-shortcuts/teams';
import { assets } from '@atlassian/global-shortcuts/assets';
import {
	L1_MENU_ID,
	type MenuItemConfigData,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/types.tsx';

import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useLicenseStates } from '@atlassian/jira-tenant-context-controller/src/components/license-states/index.tsx';
import messages from './messages.tsx';
import { useOrderedAppShortcuts } from './useOrderedAppShortcuts.tsx';

type GlobalShortcutsProps = {
	appItemsData?: MenuItemConfigData[];
};
export function GlobalShortcuts({ appItemsData }: GlobalShortcutsProps) {
	const intl = useIntl();
	const cloudId = useCloudId();
	const { data: orgId } = useOrgId();
	const licenceStates = useLicenseStates();

	// Map shortcut configs to menu identifiers
	const shortcutsMap = useMemo(() => {
		return {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence(() => ''),
			[L1_MENU_ID.GLOBAL_APP_LOOM]: loom(() => ''),
			[L1_MENU_ID.GLOBAL_APP_GOALS]: goals(
				intl.formatMessage(messages.goalsShortcutText),
				() => 'https://home.atlassian.com/goals',
			),
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projects(
				intl.formatMessage(messages.projectsShortcutText),
				() => 'https://home.atlassian.com/projects',
			),
			[L1_MENU_ID.GLOBAL_APP_TEAMS]: teams(
				// TODO: Reintroduce translations
				// intl.formatMessage(messages.teamsShortcutText),
				() => '/jira/people/search',
			),
			[L1_MENU_ID.GLOBAL_APP_ASSETS]: assets(
				intl.formatMessage(messages.assetsShortcutText),
				() => '/jira/assets',
			),
			[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie(() => ''),
		};
	}, [intl]);

	// Filter product shortcuts based on licence states
	const licenceFilterMap = useMemo((): { [key: string]: GlobalShortcutsItem } => {
		if (licenceStates.core === 'ACTIVE') {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
					[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					[L1_MENU_ID.GLOBAL_APP_TEAMS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_TEAMS],
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
					[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
					[L1_MENU_ID.GLOBAL_APP_TEAMS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_TEAMS],
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		} else {
			if (licenceStates.serviceDesk === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_ASSETS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_ASSETS],
					[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_OPSGENIE],
					[L1_MENU_ID.GLOBAL_APP_TEAMS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_TEAMS],
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}

			if (licenceStates.productDiscovery === 'ACTIVE') {
				return {
					[L1_MENU_ID.GLOBAL_APP_TEAMS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_TEAMS],
					[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
				};
			}
		}

		return {
			[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_CONFLUENCE],
			[L1_MENU_ID.GLOBAL_APP_LOOM]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_LOOM],
			[L1_MENU_ID.GLOBAL_APP_TEAMS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_TEAMS],
			[L1_MENU_ID.GLOBAL_APP_GOALS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_GOALS],
			[L1_MENU_ID.GLOBAL_APP_PROJECTS]: shortcutsMap[L1_MENU_ID.GLOBAL_APP_PROJECTS],
		};
	}, [shortcutsMap, licenceStates]);

	// sort filtered products based on order of appItemsData
	const orderedShortcuts = useOrderedAppShortcuts(licenceFilterMap, appItemsData);

	return (
		<GlobalShortcutsComponent
			cloudId={cloudId}
			orgId={orgId}
			useSkeleton
			promoteSite
			shortcutItems={orderedShortcuts}
			product="jira"
		/>
	);
}
